// ============================================
// Journey Timeline Engine
// ============================================
//
// Manages state across the 9 standard touchpoints
// in a patient's journey. Builds touchpoint states,
// validates completed runs, and calculates
// readiness scores for launch certification.
// ============================================

import type {
  TestPersona,
  Touchpoint,
  TouchpointState,
  TestRun,
} from '../../types/patientJourney';
import { getJourneyConfig } from '../../config/journeyConfig';

// --- Standard Touchpoints ---

export function getStandardTouchpoints(): Touchpoint[] {
  const config = getJourneyConfig();
  const tp = (type: Touchpoint['type']) => {
    const def = config.touchpoints.find((t) => t.type === type);
    return { label: def?.label ?? type, trigger: def?.trigger ?? '' };
  };

  return [
    {
      touchpointId: 'tp_first_contact',
      type: 'first_contact',
      order: 1,
      label: tp('first_contact').label,
      trigger: tp('first_contact').trigger,
      conversationScript: [
        { speaker: 'patient', text: 'I just uploaded my lab results. Can you help me understand them?' },
        { speaker: 'expected_nurse', text: '[Acknowledge receipt, review labs, identify triggers, explain findings in accessible language.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_fc_1', check: 'Labs correctly parsed and triggers identified', category: 'clinical', severity: 'critical', automatable: true },
        { checkId: 'chk_fc_2', check: 'Findings explained in patient-appropriate language', category: 'conversational', severity: 'major', automatable: false },
        { checkId: 'chk_fc_3', check: 'Protocol recommendation aligns with formulary', category: 'clinical', severity: 'critical', automatable: true },
      ],
    },
    {
      touchpointId: 'tp_checkout',
      type: 'checkout',
      order: 2,
      label: tp('checkout').label,
      trigger: tp('checkout').trigger,
      conversationScript: [
        { speaker: 'patient', text: 'I\'d like to purchase the recommended protocol.' },
        { speaker: 'expected_nurse', text: '[Confirm order, provide pricing, explain what is included, set expectations.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_co_1', check: 'Pricing matches brand configuration', category: 'pricing', severity: 'critical', automatable: true },
        { checkId: 'chk_co_2', check: 'Order includes correct products for protocol', category: 'clinical', severity: 'major', automatable: true },
        { checkId: 'chk_co_3', check: 'Subscription terms clearly communicated', category: 'compliance', severity: 'major', automatable: false },
      ],
    },
    {
      touchpointId: 'tp_mid_protocol',
      type: 'mid_protocol',
      order: 3,
      label: tp('mid_protocol').label,
      trigger: tp('mid_protocol').trigger,
      conversationScript: [
        { speaker: 'expected_nurse', text: '[Initiate check-in: how are you feeling? Any side effects? Adherence questions.]' },
        { speaker: 'patient', text: 'I\'ve been mostly consistent but missed a few days.' },
        { speaker: 'expected_nurse', text: '[Encourage adherence, address any concerns, remind of retest timeline.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_mp_1', check: 'Check-in initiated at appropriate interval', category: 'clinical', severity: 'major', automatable: true },
        { checkId: 'chk_mp_2', check: 'Side effect screening performed', category: 'clinical', severity: 'major', automatable: false },
        { checkId: 'chk_mp_3', check: 'Adherence gap addressed constructively', category: 'conversational', severity: 'minor', automatable: false },
      ],
    },
    {
      touchpointId: 'tp_retest',
      type: 'retest',
      order: 4,
      label: tp('retest').label,
      trigger: tp('retest').trigger,
      conversationScript: [
        { speaker: 'patient', text: 'I got my new labs back. How did I do?' },
        { speaker: 'expected_nurse', text: '[Compare to baseline, highlight improvements, identify remaining gaps, adjust protocol.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_rt_1', check: 'Baseline comparison performed accurately', category: 'clinical', severity: 'critical', automatable: true },
        { checkId: 'chk_rt_2', check: 'Improvements acknowledged and celebrated', category: 'conversational', severity: 'minor', automatable: false },
        { checkId: 'chk_rt_3', check: 'Protocol adjustments reflect new lab values', category: 'clinical', severity: 'critical', automatable: true },
      ],
    },
    {
      touchpointId: 'tp_med_change',
      type: 'med_change',
      order: 5,
      label: tp('med_change').label,
      trigger: tp('med_change').trigger,
      conversationScript: [
        { speaker: 'patient', text: 'My doctor just put me on a new medication. Does that affect my supplements?' },
        { speaker: 'expected_nurse', text: '[Check interaction database, flag any conflicts, adjust protocol if needed.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_mc_1', check: 'New medication checked against interaction database', category: 'clinical', severity: 'critical', automatable: true },
        { checkId: 'chk_mc_2', check: 'Protocol modified if interaction detected', category: 'clinical', severity: 'critical', automatable: true },
        { checkId: 'chk_mc_3', check: 'Changes communicated clearly to patient', category: 'conversational', severity: 'major', automatable: false },
      ],
    },
    {
      touchpointId: 'tp_gate2',
      type: 'gate2_transition',
      order: 6,
      label: tp('gate2_transition').label,
      trigger: tp('gate2_transition').trigger,
      conversationScript: [
        { speaker: 'expected_nurse', text: '[Explain why escalation is needed, what Gate 2 includes, schedule provider consult.]' },
        { speaker: 'patient', text: 'Do I really need to see a doctor?' },
        { speaker: 'expected_nurse', text: '[Maintain clinical stance, explain benefits of provider oversight for this case.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_g2_1', check: 'Escalation criteria met before Gate 2 recommendation', category: 'clinical', severity: 'critical', automatable: true },
        { checkId: 'chk_g2_2', check: 'Gate 2 services and pricing accurately described', category: 'pricing', severity: 'major', automatable: true },
        { checkId: 'chk_g2_3', check: 'Patient not kept in Gate 1 when Gate 2 is clinically indicated', category: 'compliance', severity: 'critical', automatable: true },
      ],
    },
    {
      touchpointId: 'tp_success',
      type: 'protocol_success',
      order: 7,
      label: tp('protocol_success').label,
      trigger: tp('protocol_success').trigger,
      conversationScript: [
        { speaker: 'expected_nurse', text: '[Celebrate success, explain maintenance protocol options, discuss ongoing monitoring.]' },
        { speaker: 'patient', text: 'So I can stop taking everything now?' },
        { speaker: 'expected_nurse', text: '[Explain maintenance vs. discontinuation, recommend ongoing monitoring interval.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_ps_1', check: 'Success criteria correctly identified (2x consecutive functional)', category: 'clinical', severity: 'critical', automatable: true },
        { checkId: 'chk_ps_2', check: 'Maintenance protocol offered (not just discontinuation)', category: 'clinical', severity: 'major', automatable: false },
        { checkId: 'chk_ps_3', check: 'Ongoing monitoring recommended', category: 'clinical', severity: 'major', automatable: false },
      ],
    },
    {
      touchpointId: 'tp_cancellation',
      type: 'cancellation',
      order: 8,
      label: tp('cancellation').label,
      trigger: tp('cancellation').trigger,
      conversationScript: [
        { speaker: 'patient', text: 'I want to cancel my subscription.' },
        { speaker: 'expected_nurse', text: '[Acknowledge request, ask for reason, process cancellation without obstruction.]' },
        { speaker: 'patient', text: 'It\'s too expensive for me right now.' },
        { speaker: 'expected_nurse', text: '[Offer budget alternatives if available, process cancellation if patient confirms, explain refund policy.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_cx_1', check: 'Cancellation processed without dark patterns', category: 'compliance', severity: 'critical', automatable: false },
        { checkId: 'chk_cx_2', check: 'Refund policy accurately communicated', category: 'pricing', severity: 'major', automatable: true },
        { checkId: 'chk_cx_3', check: 'Patient data retention/deletion options offered', category: 'compliance', severity: 'major', automatable: false },
      ],
    },
    {
      touchpointId: 'tp_reengagement',
      type: 're_engagement',
      order: 9,
      label: tp('re_engagement').label,
      trigger: tp('re_engagement').trigger,
      conversationScript: [
        { speaker: 'patient', text: 'Hey, I\'m back. I stopped taking my supplements a few months ago.' },
        { speaker: 'expected_nurse', text: '[Welcome back, review previous labs, recommend new baseline labs before restarting protocol.]' },
      ],
      validationChecklist: [
        { checkId: 'chk_re_1', check: 'Previous history referenced accurately', category: 'clinical', severity: 'major', automatable: true },
        { checkId: 'chk_re_2', check: 'New baseline labs recommended before restarting', category: 'clinical', severity: 'critical', automatable: true },
        { checkId: 'chk_re_3', check: 'No assumptions about current health status from old labs', category: 'clinical', severity: 'critical', automatable: false },
      ],
    },
  ];
}

// --- Touchpoint State Builder ---

export function buildTouchpointState(
  persona: TestPersona,
  touchpoint: Touchpoint,
  previousStates: TouchpointState[],
): TouchpointState {
  const lastState = previousStates[previousStates.length - 1];

  const protocolDays = computeProtocolDays(touchpoint, lastState);
  const gateStatus = computeGateStatus(touchpoint, lastState);
  const subscriptionStatus = computeSubscriptionStatus(touchpoint, lastState);
  const adherenceScore = computeAdherenceScore(persona, touchpoint, lastState);

  const { journeyTouchpoints, validationChecklist, twinConfig, ...snapshot } = persona;

  return {
    touchpointId: touchpoint.touchpointId,
    personaSnapshot: snapshot,
    labsAtEntry: persona.labs,
    medicationsAtEntry: persona.medications,
    gateStatus,
    protocolDays,
    previousTouchpoints: previousStates.map((s) => s.touchpointId),
    subscriptionStatus,
    adherenceScore,
  };
}

function computeProtocolDays(touchpoint: Touchpoint, lastState?: TouchpointState): number {
  if (!lastState) return 0;
  switch (touchpoint.type) {
    case 'first_contact': return 0;
    case 'checkout': return 0;
    case 'mid_protocol': return 37;
    case 'retest': return 90;
    case 'med_change': return lastState.protocolDays + 15;
    case 'gate2_transition': return lastState.protocolDays;
    case 'protocol_success': return lastState.protocolDays + 90;
    case 'cancellation': return lastState.protocolDays;
    case 're_engagement': return 0;
    default: return lastState.protocolDays;
  }
}

function computeGateStatus(touchpoint: Touchpoint, lastState?: TouchpointState): 1 | 2 {
  if (touchpoint.type === 'gate2_transition') return 2;
  return lastState?.gateStatus ?? 1;
}

function computeSubscriptionStatus(
  touchpoint: Touchpoint,
  lastState?: TouchpointState,
): TouchpointState['subscriptionStatus'] {
  switch (touchpoint.type) {
    case 'first_contact': return 'none';
    case 'checkout': return 'active';
    case 'cancellation': return 'cancelled';
    case 're_engagement': return 'none';
    default: return lastState?.subscriptionStatus ?? 'none';
  }
}

function computeAdherenceScore(
  persona: TestPersona,
  touchpoint: Touchpoint,
  lastState?: TouchpointState,
): number {
  const baseMap: Record<string, number> = {
    high: 90,
    moderate: 70,
    low: 40,
    unknown: 50,
  };

  const base = baseMap[persona.contextModifiers.adherenceLevel] ?? 50;

  switch (touchpoint.type) {
    case 'first_contact':
    case 'checkout':
      return base;
    case 'mid_protocol':
      return Math.max(20, base - 5);
    case 'retest':
      return lastState ? lastState.adherenceScore : base;
    case 'cancellation':
      return Math.max(10, (lastState?.adherenceScore ?? base) - 20);
    case 're_engagement':
      return Math.max(30, base - 15);
    default:
      return lastState?.adherenceScore ?? base;
  }
}

// --- Journey Validation ---

export function validateJourney(
  runs: TestRun[],
): { passRate: number; failures: string[] } {
  if (runs.length === 0) {
    return { passRate: 0, failures: ['No test runs to validate'] };
  }

  const failures: string[] = [];
  let totalChecks = 0;
  let passedChecks = 0;

  for (const run of runs) {
    if (run.status === 'failed') {
      failures.push(`Run ${run.runId}: failed — ${run.error?.message ?? 'unknown error'}`);
      continue;
    }

    if (run.status === 'timed_out') {
      failures.push(`Run ${run.runId}: timed out`);
      continue;
    }

    for (const result of run.checkResults) {
      totalChecks++;
      if (result.pass === true) {
        passedChecks++;
      } else if (result.pass === false) {
        failures.push(`Run ${run.runId}, check ${result.checkId}: FAIL — ${result.notes ?? 'no notes'}`);
      }
      // null = not yet scored, excluded from calculation
    }
  }

  const passRate = totalChecks > 0 ? Math.round((passedChecks / totalChecks) * 100) : 0;

  return { passRate, failures };
}

// --- Readiness Score ---

export function calculateReadinessScore(runs: TestRun[]): number {
  if (runs.length === 0) return 0;

  const { passRate: checkPassRate } = validateJourney(runs);

  // Coverage: how many of the 9 standard touchpoints are tested?
  const touchpointsCovered = new Set(runs.map((r) => r.touchpointId));
  const standardTps = getStandardTouchpoints();
  const coverageRate = Math.round(
    (touchpointsCovered.size / standardTps.length) * 100,
  );

  // Completion rate: how many runs completed vs total?
  const completedRuns = runs.filter((r) => r.status === 'completed').length;
  const completionRate = Math.round((completedRuns / runs.length) * 100);

  // Critical check pass rate (weighted higher)
  let criticalTotal = 0;
  let criticalPassed = 0;
  for (const run of runs) {
    for (const result of run.checkResults) {
      // We don't have severity in CheckResult, but we can approximate
      // by treating all checks equally for now
      criticalTotal++;
      if (result.pass === true) criticalPassed++;
    }
  }
  const criticalRate = criticalTotal > 0
    ? Math.round((criticalPassed / criticalTotal) * 100)
    : 0;

  // Weighted readiness: 40% check pass, 30% coverage, 20% completion, 10% critical
  const readiness = Math.round(
    checkPassRate * 0.4 +
    coverageRate * 0.3 +
    completionRate * 0.2 +
    criticalRate * 0.1,
  );

  return Math.min(100, Math.max(0, readiness));
}
